<template>
  <div class="panel achievement-award-container" :class="displayClass">
    <div class="achievement-image">
      <img :src="getAchievementImage(achievement)" alt="Achievement award">
    </div>
    <div class="achievement-meta">
      <div class="achievement-meta-left">
        <h3>{{ achievement.name }}</h3>
        <p>{{ achievement.description }}</p>
      </div>
      <div class="achievement-meta-right">
        <div v-if="hasBeenAwarded" class="achievement-awarded">
          <span class="award-date">Awarded: {{ dateOfIssue }}</span>
          <k-link v-if="showCertificateLink"
            :href="certificateUrl"
            :text="`View ${achievementTypePretty}`"
          ></k-link>
          <span v-if="achievement.shared_on_linked_in">{{ achievementTypePretty }} added to LinkedIn
            <i class="fa-brands fa-linkedin" aria-hidden="true"></i>
          </span>
          <k-link
            :href="achievement.linkedin_url"
            :text="addBadgeText"
            :aria-label="tooltipText"
            fa-icon="fa-brands fa-linkedin"
            @click="shareOnLinkedIn"
          ></k-link>
        </div>
        <div v-else class="achievement-not-awarded">
          <span>Not yet achieved</span>
        </div>
        <div v-if="revocable && hasBeenAwarded" class="revoke-buttons">
          <button v-if="achievementType === 'badge' && this.$permissions.hasPermission('revoke_programme_badge')"
            @click="$emit('revoke-achievement', achievement.programme_badge_id, achievementType)"
            class="btn btn-danger revoke-badge-button"
          >Revoke</button>
          <button v-if="achievementType === 'certificate' && this.$permissions.hasPermission('revoke_programme_certificate')"
            @click="$emit('revoke-achievement', achievement.programme_id, achievementType)"
            class="btn btn-danger revoke-certificate-button"
          >Revoke</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.achievement-award-container,
.achievement-image,
.achievement-meta,
.achievement-meta-right {
  display: flex;
  gap: 15px;
  align-items: center;
  position: relative;
  justify-content: space-between;
}

.achievement-award-container {
  padding: 5px 20px 5px 5px;
  min-height: 110px;
}

.achievement-image {
  width: 100px;
}

.achievement-image > img {
  width: 100px;
}

.achievement-meta {
  width: calc(100% - 120px);
  display: flex;
}

.achievement-awarded,
.achievement-not-awarded {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.k-link {
  margin: 0.3em 0;
}
</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import KLink from '@base-components/k-link.vue';
import { GOOGLE_CLOUD_STORAGE } from '@constants';
import getEnvironmentConfig from '../../get-environment-config';

const envConfig = getEnvironmentConfig();

export default {
  components: { KLink },
  mixins: [TimeMixin],
  props: {
    achievement: {
      type: Object,
      required: true,
    },
    achievementType: {
      type: String,
      default: 'badge',
    },
    revocable: {
      type: Boolean,
      default: false,
    },
    showCertificateLink: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    hasBeenAwarded() {
      return this.achievement.has_been_awarded;
    },
    displayClass() {
      return this.hasBeenAwarded ? '' : 'disabled';
    },
    dateOfIssue() {
      if (this.hasBeenAwarded) {
        return this.parseTimestamp(this.achievement.date_issued, undefined, 'MMM d, yyyy');
      }
      return null;
    },
    programmeAchievementId() {
      if (this.achievementType === 'badge') {
        return this.achievement.programme_badge_id;
      }
      return this.achievement.programme_id;
    },
    tooltipText() {
      return `Add ${this.achievementType} to your LinkedIn profile`;
    },
    addBadgeText() {
      return this.achievement.shared_on_linked_in ? 'Re-add to LinkedIn' : 'Add to LinkedIn';
    },
    achievementTypePretty() {
      return this.achievementType === 'badge' ? 'Badge' : 'Certificate';
    },
    certificateUrl() {
      return `https://${envConfig.certificateDomain}/certificate/${this.achievement.uuid}`;
    },
  },
  methods: {
    getAchievementImage(achievement) {
      return `${GOOGLE_CLOUD_STORAGE}${achievement.resource_link}`;
    },
    shareOnLinkedIn() {
      this.$logger.info(`Sharing ${this.achievementType}s on Linkedin`, { programmeAchievementId: this.programmeAchievementId });
      this.$http.put(`/api/profile/programme/${this.achievementType}/${this.programmeAchievementId}/linkedin`).then(() => {
        this.$logger.info(`Shared ${this.achievementType} on LinkedIn`, { programmeAchievementId: this.programmeAchievementId });
      }).catch(err => {
        this.$logger.autowarn(`Error sharing ${this.achievementType}s on Linkedin`, { programmeAchievementId: this.programmeAchievementId }, err);
      });
    },
  },
};
</script>
